import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Table, 
  Typography, 
  Spin, 
  Alert,
  DatePicker,
  Select,
  Space,
  Button
} from 'antd';
import { 
  UserOutlined, 
  EyeOutlined, 
  BarChartOutlined, 
  RiseOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import axios from 'axios';
import { getStatisticsOverview, getTaskStatistics, getUserActivities } from '../../utils/taskStatistics';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

interface OverviewStats {
  period: {
    start_date: string;
    end_date: string;
    days: number;
  };
  tasks: {
    total: number;
    successful: number;
    failed: number;
    success_rate: number;
    avg_execution_time: number;
  };
  activities: {
    total: number;
    unique_visitors: number;
    unique_users: number;
  };
  services: {
    total_records: number;
    total_requests: number;
    successful_requests: number;
    avg_execution_time: number;
  };
}

interface DailyVisit {
  date: string;
  visits: number;
}

interface WeeklyVisit {
  week: string;
  visits: number;
}

interface MonthlyVisit {
  month: string;
  visits: number;
}

interface TopPage {
  page: string;
  visits: number;
}

const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [overviewStats, setOverviewStats] = useState<OverviewStats | null>(null);
  const [dailyVisits, setDailyVisits] = useState<DailyVisit[]>([]);
  const [weeklyVisits, setWeeklyVisits] = useState<WeeklyVisit[]>([]);
  const [monthlyVisits, setMonthlyVisits] = useState<MonthlyVisit[]>([]);
  const [topPages, setTopPages] = useState<TopPage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  // 获取概览统计数据
  const fetchOverviewStats = async () => {
    try {
      const data = await getStatisticsOverview(7);
      setOverviewStats(data);
    } catch (err) {
      setError('获取概览统计数据失败');
      console.error('获取概览统计数据失败:', err);
    }
  };

  // 获取任务统计
  const fetchTaskStats = async () => {
    try {
      const data = await getTaskStatistics(30);
      // 转换数据格式以适配现有图表
      const dailyData = data.daily_stats.map(item => ({
        date: item.date,
        visits: item.total
      }));
      setDailyVisits(dailyData);
    } catch (err) {
      console.error('获取任务统计数据失败:', err);
    }
  };

  // 获取用户活动统计
  const fetchActivityStats = async () => {
    try {
      const data = await getUserActivities(7);
      // 转换数据格式以适配现有图表
      const weeklyData = data.daily_activities.map(item => ({
        week: item.date,
        visits: item.total
      }));
      setWeeklyVisits(weeklyData);
    } catch (err) {
      console.error('获取用户活动统计数据失败:', err);
    }
  };

  // 获取服务使用统计（替代每月统计）
  const fetchServiceStats = async () => {
    try {
      // 暂时使用空数据，因为新API还没有月度统计
      setMonthlyVisits([]);
    } catch (err) {
      console.error('获取服务统计数据失败:', err);
    }
  };

  // 获取页面访问统计（暂时使用空数据）
  const fetchPageStats = async () => {
    try {
      // 新系统暂时没有页面统计，使用空数据
      setTopPages([]);
    } catch (err) {
      console.error('获取页面统计数据失败:', err);
    }
  };

  // 手动刷新数据
  const handleRefresh = async () => {
    setLoading(true);
    setError(null);
    await Promise.all([
      fetchOverviewStats(),
      fetchTaskStats(),
      fetchActivityStats(),
      fetchServiceStats(),
      fetchPageStats()
    ]);
    setLastRefresh(new Date());
    setLoading(false);
  };

  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      await Promise.all([
        fetchOverviewStats(),
        fetchTaskStats(),
        fetchActivityStats(),
        fetchServiceStats(),
        fetchPageStats()
      ]);
      setLoading(false);
    };

    fetchAllData();
    
    // 设置定时刷新，每5分钟刷新一次，而不是频繁刷新
    const interval = setInterval(() => {
      handleRefresh();
    }, 5 * 60 * 1000); // 5分钟

    return () => clearInterval(interval);
  }, []);

  // 任务统计图表配置
  const getDailyChartOption = () => ({
    title: {
      text: '每日任务执行趋势',
      left: 'center'
    },
    tooltip: {
      trigger: 'axis'
    },
    xAxis: {
      type: 'category',
      data: dailyVisits.map(item => item.date)
    },
    yAxis: {
      type: 'value'
    },
    series: [{
      data: dailyVisits.map(item => item.visits),
      type: 'line',
      smooth: true,
      areaStyle: {
        opacity: 0.3
      }
    }]
  });

  // 用户活动图表配置
  const getWeeklyChartOption = () => ({
    title: {
      text: '用户活动统计',
      left: 'center'
    },
    tooltip: {
      trigger: 'axis'
    },
    xAxis: {
      type: 'category',
      data: weeklyVisits.map(item => `第${item.week.split('-')[1]}周`)
    },
    yAxis: {
      type: 'value'
    },
    series: [{
      data: weeklyVisits.map(item => item.visits),
      type: 'bar'
    }]
  });

  // 服务使用图表配置
  const getMonthlyChartOption = () => ({
    title: {
      text: '服务使用统计',
      left: 'center'
    },
    tooltip: {
      trigger: 'axis'
    },
    xAxis: {
      type: 'category',
      data: monthlyVisits.map(item => item.month)
    },
    yAxis: {
      type: 'value'
    },
    series: [{
      data: monthlyVisits.map(item => item.visits),
      type: 'bar',
      itemStyle: {
        color: '#1890ff'
      }
    }]
  });

  // 页面访问排行表格列配置
  const pageColumns = [
    {
      title: '页面',
      dataIndex: 'page',
      key: 'page',
      render: (text: string) => <Text copyable>{text}</Text>
    },
    {
      title: '访问量',
      dataIndex: 'visits',
      key: 'visits',
      sorter: (a: TopPage, b: TopPage) => a.visits - b.visits,
      render: (visits: number) => (
        <Text strong style={{ color: '#1890ff' }}>
          {visits.toLocaleString()}
        </Text>
      )
    }
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>加载统计数据中...</div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="数据加载失败"
        description={error}
        type="error"
        showIcon
        style={{ marginBottom: 16 }}
        action={
          <Button size="small" danger onClick={handleRefresh}>
            重试
          </Button>
        }
      />
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2}>数据统计仪表盘</Title>
        <Space>
          <Text type="secondary">
            最后更新: {lastRefresh.toLocaleTimeString()}
          </Text>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={handleRefresh}
            loading={loading}
          >
            刷新数据
          </Button>
        </Space>
      </div>
      
      {/* 概览统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="总任务数"
              value={overviewStats?.tasks.total || 0}
              prefix={<EyeOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="活跃用户"
              value={overviewStats?.activities.unique_users || 0}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="成功任务"
              value={overviewStats?.tasks.successful || 0}
              prefix={<RiseOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="成功率"
              value={overviewStats?.tasks.success_rate ? overviewStats.tasks.success_rate.toFixed(1) : 0}
              suffix="%"
              prefix={<CalendarOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 时间段统计 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="总请求数"
              value={overviewStats?.services.total_requests || 0}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="平均执行时间"
              value={overviewStats?.tasks.avg_execution_time ? overviewStats.tasks.avg_execution_time.toFixed(2) : 0}
              suffix="秒"
              prefix={<BarChartOutlined />}
              valueStyle={{ color: '#13c2c2' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Statistic
              title="失败任务"
              value={overviewStats?.tasks.failed || 0}
              prefix={<BarChartOutlined />}
              valueStyle={{ color: '#eb2f96' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 图表区域 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <Card title="任务执行趋势" style={{ height: 400 }}>
            <ReactECharts 
              option={getDailyChartOption()} 
              style={{ height: 300 }}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="用户活动统计" style={{ height: 400 }}>
            <ReactECharts 
              option={getWeeklyChartOption()} 
              style={{ height: 300 }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <Card title="每月统计" style={{ height: 400 }}>
            <ReactECharts 
              option={getMonthlyChartOption()} 
              style={{ height: 300 }}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="页面访问排行" style={{ height: 400 }}>
            <Table
              dataSource={topPages}
              columns={pageColumns}
              pagination={false}
              size="small"
              scroll={{ y: 250 }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard; 